// Eased horizontal scrolling for the home page's rows.
//
// Setting scrollLeft straight from a wheel delta or a pointer position moves
// the row exactly as far as the input said, the instant it said it — which is
// accurate and feels abrupt. This keeps a target and walks the row toward it a
// fraction at a time, so a wheel tick glides to a stop and a released drag
// carries on before settling.
//
// It never animates DURING a drag: a finger or a cursor on the row should move
// it one-to-one, or the row feels like it is lagging behind the hand. Easing
// belongs to what happens after the input stops.

// How much of the remaining distance is covered per frame. Lower is more
// relaxed; high enough that a long throw still arrives promptly.
const EASE = 0.12;
// Under this many pixels left, snap and stop — otherwise the last fraction of
// a pixel keeps a rAF alive forever.
const EPSILON = 0.5;
// Release velocity (px/ms) is multiplied by this to decide how far a throw
// carries.
const THROW = 170;
// Ignore a flick slower than this; it is a slow drag ending, not a throw.
const MIN_THROW_V = 0.08;

export function createGlider(el) {
  let target = 0;
  let raf = 0;
  let running = false;

  const maxScroll = () => Math.max(0, el.scrollWidth - el.clientWidth);
  const clamp = (v) => Math.min(Math.max(v, 0), maxScroll());

  const stop = () => {
    running = false;
    cancelAnimationFrame(raf);
    raf = 0;
  };

  const tick = () => {
    const diff = target - el.scrollLeft;
    if (Math.abs(diff) < EPSILON) {
      el.scrollLeft = target;
      stop();
      return;
    }
    el.scrollLeft += diff * EASE;
    raf = requestAnimationFrame(tick);
  };

  const start = () => {
    if (running) return;
    running = true;
    raf = requestAnimationFrame(tick);
  };

  return {
    /** Add to the glide's destination — one wheel tick, say. */
    nudge(delta) {
      // Compounds off the live target while gliding, so spinning a wheel
      // accelerates instead of restarting from wherever the row happens to be.
      target = clamp((running ? target : el.scrollLeft) + delta);
      start();
    },
    /** Let a released drag carry on at `velocity` px/ms. */
    settle(velocity) {
      if (Math.abs(velocity) < MIN_THROW_V) return;
      target = clamp(el.scrollLeft + velocity * THROW);
      start();
    },
    /** Hand control back, e.g. when a drag takes over. */
    cancel() {
      stop();
      target = el.scrollLeft;
    },
    destroy: stop,
  };
}
